import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteProvider } from './context/SiteContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SessionRecovery } from './components/SessionRecovery';
import ScrollDebugBanner from './components/ScrollDebugBanner';

// ── Auth pages — eager (always needed) ──────────────────────────────────
import LoginPage from './pages/LoginPage';
import CompanySignupPage from './pages/CompanySignupPage';
import EmployeeSignupPage from './pages/EmployeeSignupPage';
import UnknownRoutePage from './pages/UnknownRoutePage';

// ── B5: Prefetch top routes on idle after login ─────────────────────────
const prefetchRoutes = () => {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => {
      import(/* webpackChunkName: "ledger" */ './pages/LedgersPage');
      import(/* webpackChunkName: "payments" */ './pages/PaymentsPage');
      import(/* webpackChunkName: "bills" */ './pages/BillsPage');
    });
  }
};

// ── ALL other pages — lazy (loaded only when visited) ───────────────────
// This splits the bundle into 80+ small chunks → 60-70% faster initial load
const DashboardPage          = React.lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/DashboardPage'));
const ChallansPage           = React.lazy(() => import('./pages/ChallansPage'));
const ChallanFormPage        = React.lazy(() => import('./pages/ChallanFormPage'));
const BillsPage              = React.lazy(() => import('./pages/BillsPage'));
const BillFormPage           = React.lazy(() => import('./pages/BillFormPage'));
const PaymentsPage           = React.lazy(() => import('./pages/PaymentsPage'));
const PaymentFormPage        = React.lazy(() => import('./pages/PaymentFormPage'));
const PaymentDetailPage      = React.lazy(() => import('./pages/PaymentDetailPage'));
const ExpensesPage           = React.lazy(() => import('./pages/ExpensesPage'));
const ExpenseFormPage        = React.lazy(() => import('./pages/ExpenseFormPage'));
const ExpenseDetailPage      = React.lazy(() => import('./pages/ExpenseDetailPage'));
const ReceiptsPage           = React.lazy(() => import('./pages/ReceiptsPage'));
const ReceiptFormPage        = React.lazy(() => import('./pages/ReceiptFormPage'));
const ReceiptDetailPage      = React.lazy(() => import('./pages/ReceiptDetailPage'));
const AccessControlPage      = React.lazy(() => import('./pages/AccessControlPage'));
const GlobalAuditPage        = React.lazy(() => import('./pages/GlobalAuditPage'));
const SourceReportsPage      = React.lazy(() => import('./pages/SourceReportsPage'));
const VehicleFuelPage        = React.lazy(() => import('./pages/VehicleFuelPage'));
const CheckingPage           = React.lazy(() => import('./pages/CheckingPage'));
const LedgersPage            = React.lazy(() => import('./pages/LedgersPage'));
const ReportsPage            = React.lazy(() => import('./pages/ReportsPage'));
const ChallanDetailPage      = React.lazy(() => import('./pages/ChallanDetailPage'));
const BillDetailPage         = React.lazy(() => import('./pages/BillDetailPage'));
const UsersPage              = React.lazy(() => import('./pages/UsersPage'));
const PartiesPage            = React.lazy(() => import('./pages/PartiesPage'));
const ProjectsSitesPage      = React.lazy(() => import('./pages/ProjectsSitesPage'));
const ItemsPage              = React.lazy(() => import('./pages/ItemsPage'));
const BanksPage              = React.lazy(() => import('./pages/BanksPage'));
const OpeningBalancesPage = React.lazy(() => import('./pages/OpeningBalancesPage'));
const DesignationsPage       = React.lazy(() => import('./pages/DesignationsPage'));
const LoansPage              = React.lazy(() => import('./pages/LoansPage'));
const StaffAdvancesPage      = React.lazy(() => import('./pages/StaffAdvancesPage'));
const AuditTrailPage         = React.lazy(() => import('./pages/AuditTrailPage'));
const MastersPage            = React.lazy(() => import('./pages/MastersPage'));
const MappingsPage           = React.lazy(() => import('./pages/MappingsPage'));
const InventoryDashboardPage = React.lazy(() => import('./pages/InventoryDashboardPage'));
const InventoryStockPage     = React.lazy(() => import('./pages/InventoryStockPage'));
const StockLedgerPage        = React.lazy(() => import('./pages/StockLedgerPage'));
const InventoryReportsPage   = React.lazy(() => import('./pages/InventoryReportsPage'));
const GRNFormPage            = React.lazy(() => import('./pages/GRNFormPage'));
const ConsumptionFormPage    = React.lazy(() => import('./pages/ConsumptionFormPage'));
const ConsumptionDetailPage  = React.lazy(() => import('./pages/ConsumptionDetailPage'));
const TransferFormPage       = React.lazy(() => import('./pages/TransferFormPage'));
const AdjustmentFormPage     = React.lazy(() => import('./pages/AdjustmentFormPage'));
const ReportsCenterPage      = React.lazy(() => import('./pages/ReportsCenterPage'));
const SimpleReportsPage      = React.lazy(() => import('./pages/SimpleReportsPage'));
const ExpenseReportsPage     = React.lazy(() => import('./pages/ExpenseReportsPage'));
const InventoryReportsNewPage= React.lazy(() => import('./pages/InventoryReportsNewPage'));
const ReportsHubPage         = React.lazy(() => import('./pages/ReportsHubPage'));
const DetailedReportsPage    = React.lazy(() => import('./pages/DetailedReportsPage'));
const StockCountPage         = React.lazy(() => import('./pages/StockCountPage'));
const SiteTransfersPage      = React.lazy(() => import('./pages/SiteTransfersPage'));
const SiteTransferFormPage   = React.lazy(() => import('./pages/SiteTransferFormPage'));
const SiteTransferDetailPage = React.lazy(() => import('./pages/SiteTransferDetailPage'));
const OutstandingPayablesPage  = React.lazy(() => import('./pages/OutstandingPayablesPage'));
const OutstandingReceivablesPage = React.lazy(() => import('./pages/OutstandingReceivablesPage'));
const SiteTransferReportsPage= React.lazy(() => import('./pages/SiteTransferReportsPage'));
const SiteTransferKPIPage    = React.lazy(() => import('./pages/SiteTransferKPIPage'));
const WalletSetupPage        = React.lazy(() => import('./pages/WalletSetupPage'));
const RoznamchaLedgerPage    = React.lazy(() => import('./pages/RoznamchaLedgerPage'));
const RoznamchaTransferPage  = React.lazy(() => import('./pages/RoznamchaTransferPage'));
const RoznamchaExpenseReviewPage = React.lazy(() => import('./pages/RoznamchaExpenseReviewPage'));
const RoznamchaReportsPage   = React.lazy(() => import('./pages/RoznamchaReportsPage'));
const P2DashboardPage        = React.lazy(() => import('./pages/P2DashboardPage'));
const P2DealFormPage         = React.lazy(() => import('./pages/P2DealFormPage'));
const P2DealDetailPage       = React.lazy(() => import('./pages/P2DealDetailPage'));
const FDPage                 = React.lazy(() => import('./pages/FDPage'));
const BGPage                 = React.lazy(() => import('./pages/BGPage'));
const FDBGReportsPage        = React.lazy(() => import('./pages/FDBGReportsPage'));
const P2ReportsPage          = React.lazy(() => import('./pages/P2ReportsPage'));
const SalaryEntryPage        = React.lazy(() => import('./pages/SalaryEntryPage'));
const SalaryHomePage         = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.SalaryHomePage })));
const StaffMasterPage        = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.StaffMasterPage })));
const PayrollRunPage         = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.PayrollRunPage })));
const SalarySlipsPage        = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.SalarySlipsPage })));
const StaffLedgerPage        = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.StaffLedgerPage })));
const SalaryReportsPage      = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.SalaryReportsPage })));
const SalaryPaymentsPage     = React.lazy(() => import('./pages/SalaryModulePage').then(m => ({ default: m.SalaryPaymentsPage })));
const BackupPage             = React.lazy(() => import('./pages/BackupPage'));
const OtherIncomePage        = React.lazy(() => import('./pages/OtherIncomePage'));
const OtherExpensesPage      = React.lazy(() => import('./pages/OtherExpensesPage'));
const CompanyReportPage      = React.lazy(() => import('./pages/CompanyReportPage'));
const BudgetPage             = React.lazy(() => import('./pages/BudgetPage'));
const MarketRatesPage        = React.lazy(() => import('./pages/MarketRatesPage'));
const WP_OverviewPage        = React.lazy(() => import('./pages/WP_OverviewPage'));
const WP_MatrixPage          = React.lazy(() => import('./pages/WP_MatrixPage'));
const WP_StageDetailPage     = React.lazy(() => import('./pages/WP_StageDetailPage'));
const WP_BOQPage             = React.lazy(() => import('./pages/WP_BOQPage'));
const WP_MaterialPlanningPage= React.lazy(() => import('./pages/WP_MaterialPlanningPage'));
const WP_ReconciliationPage  = React.lazy(() => import('./pages/WP_ReconciliationPage'));
const WP_ReportsPage         = React.lazy(() => import('./pages/WP_ReportsPage'));
const RealStockStatusPage    = React.lazy(() => import('./pages/RealStockStatusPage'));
const WP_ApprovalsPage       = React.lazy(() => import('./pages/WP_ApprovalsPage'));
const WP_TemplatePage        = React.lazy(() => import('./pages/WP_TemplatePage'));
const SeedCenterPage         = React.lazy(() => import('./pages/SeedCenterPage'));
const CIDashboardPage        = React.lazy(() => import('./pages/CIDashboardPage'));

// ── CEO Control Center pages ─────────────────────────────────────────────
const CeoLayout          = React.lazy(() => import('./components/CeoLayout'));
const CeoDashboardPage   = React.lazy(() => import('./pages/CeoDashboardPage'));
const CeoCompaniesPage   = React.lazy(() => import('./pages/CeoCompaniesPage'));
const CeoCompanyPage     = React.lazy(() => import('./pages/CeoCompanyPage'));
const CeoAlertsPage      = React.lazy(() => import('./pages/CeoAlertsPage'));
const CeoBroadcastPage   = React.lazy(() => import('./pages/CeoBroadcastPage'));
const CeoSupportPage     = React.lazy(() => import('./pages/CeoSupportPage'));
const CeoReportMigrationPage = React.lazy(() => import('./pages/CeoReportMigrationPage'));

// Layout & Components
import Layout from './components/Layout';
import { PermissionGate } from './components/PermissionGate';

// ── Page loading skeleton ─────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{
    minHeight:'100vh', display:'flex', flexDirection:'column',
    alignItems:'center', justifyContent:'center',
    background:'linear-gradient(160deg,#f8fafc,#f1f5f9)',
    gap:16,
  }}>
    <div style={{
      width:44, height:44, borderRadius:12,
      background:'linear-gradient(135deg,#0d9488,#7c3aed,#ec4899)',
      padding:3, boxShadow:'0 8px 24px rgba(13,148,136,0.2)',
      animation:'pulse 1.5s ease-in-out infinite',
    }}>
      <img src="/sitepe-logo.png" alt="" style={{ width:'100%',height:'100%',borderRadius:9,display:'block' }} />
    </div>
    <div style={{ display:'flex', gap:6 }}>
      {[0,1,2].map(i => (
        <div key={i} style={{
          width:8, height:8, borderRadius:'50%',
          background:'linear-gradient(135deg,#0d9488,#7c3aed)',
          animation:`bounce 1.2s ease-in-out infinite`,
          animationDelay:`${i*0.15}s`,
        }}/>
      ))}
    </div>
    <style>{`
      @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.6}}
      @keyframes bounce{0%,100%{transform:translateY(0);opacity:0.4}50%{transform:translateY(-6px);opacity:1}}
    `}</style>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, sessionError, token } = useAuth();
  
  // B5: Prefetch top routes after login (idle callback)
  useEffect(() => {
    if (isAuthenticated) prefetchRoutes();
  }, [isAuthenticated]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50/30 to-pink-50/20">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (sessionError && token) return <SessionRecovery />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user, sessionError, token } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-sky-50/30 to-pink-50/20">
        <div className="animate-spin w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (sessionError && token) return <SessionRecovery />;

  if (isAuthenticated) {
    // CEO users go straight to CEO dashboard, not normal app
    const isCeo = user?.is_ceo || (() => {
      try { return JSON.parse(atob((localStorage.getItem('token')||'').split('.')[1]))?.is_ceo; } catch { return false; }
    })();
    return <Navigate to={isCeo ? '/ceo' : '/dashboard'} replace />;
  }
  
  return children;
};

// CEO Guard — decode JWT directly to avoid state timing issues
const CeoGuard = () => {
  const { user, token } = useAuth();
  // Decode JWT directly (handles state timing where user not yet updated)
  const isCeo = React.useMemo(() => {
    if (user?.is_ceo) return true;
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.is_ceo === true;
    } catch { return false; }
  }, [user, token]);
  if (!isCeo) return <Navigate to="/dashboard" replace />;
  return <React.Suspense fallback={<PageLoader />}><CeoLayout /></React.Suspense>;
};

// Placeholder pages for modules not yet fully implemented
const PlaceholderPage = ({ title, description }) => (
  <div className="glass-card p-8 text-center animate-slide-in">
    <h1 className="text-2xl font-bold text-slate-800 mb-2">{title}</h1>
    <p className="text-slate-500">{description}</p>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup/company" element={<PublicRoute><CompanySignupPage /></PublicRoute>} />
      <Route path="/signup/employee" element={<PublicRoute><EmployeeSignupPage /></PublicRoute>} />

      {/* ── CEO Control Center (isolated namespace) ── */}
      <Route path="/ceo" element={<ProtectedRoute><CeoGuard /></ProtectedRoute>}>
        <Route index element={<CeoDashboardPage />} />
        <Route path="companies" element={<CeoCompaniesPage />} />
        <Route path="company/:companyId" element={<CeoCompanyPage />} />
        <Route path="broadcasts" element={<CeoBroadcastPage />} />
        <Route path="alerts" element={<CeoAlertsPage />} />
        <Route path="support" element={<CeoSupportPage />} />
        <Route path="reports" element={<CeoReportMigrationPage />} />
      </Route>
      
      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><SiteProvider><Layout /></SiteProvider></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Challans */}
        <Route path="challans" element={<PermissionGate permission="challan_view"><ChallansPage /></PermissionGate>} />
        <Route path="challans/new" element={<PermissionGate permission="challan_create"><ChallanFormPage /></PermissionGate>} />
        <Route path="challans/:id" element={<PermissionGate permission="challan_view"><ChallanDetailPage /></PermissionGate>} />
        <Route path="challans/:id/edit" element={<PermissionGate permission="challan_edit"><ChallanFormPage /></PermissionGate>} />
        
        {/* Bills */}
        <Route path="bills" element={<PermissionGate permission="bill_view"><BillsPage /></PermissionGate>} />
        <Route path="bills/new" element={<PermissionGate permission="bill_create"><BillFormPage /></PermissionGate>} />
        <Route path="bills/:id" element={<PermissionGate permission="bill_view"><BillDetailPage /></PermissionGate>} />
        <Route path="bills/:id/edit" element={<PermissionGate permission="bill_edit"><BillFormPage /></PermissionGate>} />
        
        {/* Payments */}
        <Route path="payments" element={<PermissionGate permission="payment_view"><PaymentsPage /></PermissionGate>} />
        <Route path="payments/new" element={<PermissionGate permission="payment_create"><PaymentFormPage /></PermissionGate>} />
        <Route path="payments/:id" element={<PermissionGate permission="payment_view"><PaymentDetailPage /></PermissionGate>} />
        <Route path="payments/:id/edit" element={<PermissionGate permission="payment_edit"><PaymentFormPage /></PermissionGate>} />
        
        {/* Expenses */}
        <Route path="expenses" element={<PermissionGate permission="expense_view"><ExpensesPage /></PermissionGate>} />
        <Route path="expenses/new" element={<PermissionGate permission="expense_create"><ExpenseFormPage /></PermissionGate>} />
        <Route path="expenses/:id" element={<PermissionGate permission="expense_view"><ExpenseDetailPage /></PermissionGate>} />
        <Route path="expenses/:id/edit" element={<PermissionGate permission="expense_create"><ExpenseFormPage /></PermissionGate>} />
        
        {/* Receipts */}
        <Route path="receipts" element={<PermissionGate permission="receipt_view"><ReceiptsPage /></PermissionGate>} />
        <Route path="receipts/new" element={<PermissionGate permission="receipt_create"><ReceiptFormPage /></PermissionGate>} />
        <Route path="receipts/:id" element={<PermissionGate permission="receipt_view"><ReceiptDetailPage /></PermissionGate>} />
        <Route path="inventory/vehicle-fuel" element={<PermissionGate permission="challan_view"><VehicleFuelPage /></PermissionGate>} />
        
        {/* Checking */}
        <Route path="checking" element={<PermissionGate permission="checking_view"><CheckingPage /></PermissionGate>} />
        
        {/* Ledgers */}
        <Route path="ledgers" element={<PermissionGate permission="ledger_view"><LedgersPage /></PermissionGate>} />
        
        {/* Reports */}
        <Route path="reports" element={<PermissionGate permission="report_view"><SimpleReportsPage /></PermissionGate>} />
        <Route path="reports/hub" element={<PermissionGate permission="report_view"><ReportsHubPage /></PermissionGate>} />
        <Route path="reports/advanced" element={<PermissionGate permission="report_view"><ReportsCenterPage /></PermissionGate>} />
        <Route path="reports/expenses" element={<PermissionGate permission="report_view"><ExpenseReportsPage /></PermissionGate>} />
        <Route path="reports/inventory" element={<PermissionGate permission="report_view"><InventoryReportsNewPage /></PermissionGate>} />
        <Route path="reports/detailed" element={<PermissionGate permission="report_view"><DetailedReportsPage /></PermissionGate>} />
        <Route path="reports/site-transfers" element={<PermissionGate permission="report_view"><SiteTransferReportsPage /></PermissionGate>} />
        <Route path="reports/old" element={<PermissionGate permission="report_view"><ReportsPage /></PermissionGate>} />
        <Route path="reports/loan-interest" element={<PermissionGate permission="reports.loan_interest.view"><SourceReportsPage kind="loan-interest" /></PermissionGate>} />
        <Route path="reports/bank-book" element={<PermissionGate permission="reports.bank_book.view"><SourceReportsPage kind="bank-book" /></PermissionGate>} />
        <Route path="reports/site-cost" element={<PermissionGate permission="reports.site_cost.view"><SourceReportsPage kind="site-cost" /></PermissionGate>} />
        
        {/* Site Transfers */}
        <Route path="site-transfers" element={<SiteTransfersPage />} />
        <Route path="site-transfers/new" element={<SiteTransferFormPage />} />
        <Route path="site-transfers/kpi" element={<SiteTransferKPIPage />} />
        <Route path="site-transfers/:id" element={<SiteTransferDetailPage />} />
        
        {/* Outstanding */}
        <Route path="outstanding/payables" element={<PermissionGate permission="report_view"><OutstandingPayablesPage /></PermissionGate>} />
        <Route path="outstanding/receivables" element={<PermissionGate permission="report_view"><OutstandingReceivablesPage /></PermissionGate>} />
        
        {/* Roznamcha */}
        <Route path="roznamcha/wallets" element={<WalletSetupPage />} />
        <Route path="roznamcha/ledger/:walletId" element={<RoznamchaLedgerPage />} />
        <Route path="roznamcha/transfer" element={<RoznamchaTransferPage />} />
        <Route path="roznamcha/expenses" element={<RoznamchaExpenseReviewPage />} />
        <Route path="reports/roznamcha" element={<PermissionGate permission="report_view"><RoznamchaReportsPage /></PermissionGate>} />
        
        {/* P2 */}
        <Route path="p2" element={<P2DashboardPage />} />
        <Route path="p2/new" element={<P2DealFormPage />} />
        <Route path="p2/:dealId" element={<P2DealDetailPage />} />
        
        {/* FD & BG */}
        <Route path="fd" element={<FDPage />} />
        <Route path="bg" element={<BGPage />} />
        <Route path="reports/fd-bg" element={<PermissionGate permission="report_view"><FDBGReportsPage /></PermissionGate>} />
        <Route path="reports/p2" element={<PermissionGate permission="report_view"><P2ReportsPage /></PermissionGate>} />
        
        {/* Salary */}
        <Route path="salary" element={<SalaryHomePage />} />
        <Route path="salary/staff" element={<StaffMasterPage />} />
        <Route path="salary/payroll" element={<PayrollRunPage />} />
        <Route path="salary/slips" element={<SalarySlipsPage />} />
        <Route path="salary/payments" element={<SalaryPaymentsPage />} />
        <Route path="salary/ledger" element={<StaffLedgerPage />} />
        <Route path="salary/reports" element={<SalaryReportsPage />} />
        <Route path="salary/old" element={<SalaryEntryPage />} />
        <Route path="other-income" element={<OtherIncomePage />} />
        <Route path="other-expenses" element={<OtherExpensesPage />} />
        <Route path="company-report" element={<CompanyReportPage />} />
        <Route path="budget" element={<BudgetPage />} />
        <Route path="market-rates" element={<MarketRatesPage />} />
        
        {/* Loans & Advances */}
        <Route path="loans" element={<PermissionGate permission="loan_view"><LoansPage /></PermissionGate>} />
        <Route path="advances" element={<PermissionGate permission="advance_view"><StaffAdvancesPage /></PermissionGate>} />
        <Route path="audit-trail" element={<PermissionGate permission="audit_view"><AuditTrailPage /></PermissionGate>} />
        
        {/* Inventory */}
        <Route path="inventory" element={<InventoryDashboardPage />} />
        <Route path="inventory/stock" element={<InventoryStockPage />} />
        <Route path="inventory/ledger" element={<StockLedgerPage />} />
        <Route path="inventory/ledger/:itemId" element={<StockLedgerPage />} />
        <Route path="inventory/reports" element={<InventoryReportsPage />} />
        <Route path="inventory/grn/new" element={<GRNFormPage />} />
        <Route path="inventory/consumption/new" element={<ConsumptionFormPage />} />
        <Route path="inventory/consumptions/:id/return" element={<ConsumptionDetailPage />} />
        <Route path="inventory/transfer/new" element={<TransferFormPage />} />
        <Route path="inventory/adjustment/new" element={<AdjustmentFormPage />} />
        <Route path="inventory/stock-count" element={<StockCountPage />} />
        
        {/* Settings */}
        <Route path="admin/access-control" element={<AccessControlPage />} />
        <Route path="admin/audit-logs" element={<GlobalAuditPage />} />
        <Route path="settings/projects" element={<PermissionGate permission="master_project"><ProjectsSitesPage /></PermissionGate>} />
        <Route path="settings/sites" element={<PermissionGate permission="master_site"><ProjectsSitesPage /></PermissionGate>} />
        <Route path="settings/parties" element={<PermissionGate permission="master_party"><PartiesPage /></PermissionGate>} />
        <Route path="settings/items" element={<PermissionGate permission="master_item"><ItemsPage /></PermissionGate>} />
        <Route path="settings/banks" element={<PermissionGate permission="master_bank"><BanksPage /></PermissionGate>} />
        <Route path="settings/opening-balances" element={<PermissionGate permission="masters.opening_balance.view"><OpeningBalancesPage /></PermissionGate>} />
        <Route path="settings/users" element={<PermissionGate permission="user_manage"><UsersPage /></PermissionGate>} />
        <Route path="settings/designations" element={<PermissionGate permission="designation_manage"><DesignationsPage /></PermissionGate>} />
        <Route path="settings/masters" element={<PermissionGate permission="master_item"><MastersPage /></PermissionGate>} />
        <Route path="settings/mappings" element={<PermissionGate permission="master_item"><MappingsPage /></PermissionGate>} />
        <Route path="settings/backups" element={<BackupPage />} />
        <Route path="settings/seed-center" element={<SeedCenterPage />} />
        <Route path="settings/ci-pipeline" element={<CIDashboardPage />} />
        <Route path="work-programme" element={<WP_OverviewPage />} />
        <Route path="work-programme/matrix/:siteId" element={<WP_MatrixPage />} />
        <Route path="work-programme/stage/:stageId" element={<WP_StageDetailPage />} />
        <Route path="work-programme/boq/:siteId" element={<WP_BOQPage />} />
        <Route path="work-programme/materials/:siteId" element={<WP_MaterialPlanningPage />} />
        <Route path="work-programme/reconciliation/:siteId" element={<WP_ReconciliationPage />} />
        <Route path="work-programme/reports" element={<WP_ReportsPage />} />
        <Route path="work-programme/reports/real-stock-status" element={<RealStockStatusPage />} />
        <Route path="work-programme/approvals" element={<WP_ApprovalsPage />} />
        <Route path="work-programme/templates" element={<WP_TemplatePage />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<UnknownRoutePage />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    const onUnhandled = (e) => console.error('UNHANDLED_REJECTION', e.reason);
    const onError = (e) => console.error('WINDOW_ERROR', e.error || e.message);
    window.addEventListener('unhandledrejection', onUnhandled);
    window.addEventListener('error', onError);
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandled);
      window.removeEventListener('error', onError);
    };
  }, []);

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          mobileOffset={{ top: '80px', left: '16px', right: '16px' }}
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '16px',
              padding: '16px',
            },
          }}
        />
        <ScrollDebugBanner />
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
